import React from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import nl2br from 'react-nl2br'
import styled from 'styled-components'
import { graphql, Link, ReplaceComponentRendererArgs } from 'gatsby'

import Angle from '../atoms/Angle'
import Button from '../atoms/Button'
import Heading from '../atoms/Heading'
import Image from '../atoms/Image'
import ItemizedReviewList from '../organisms/ItemizedReviewList'
import Layout from '../organisms/Layout'
import OuterLink from '../atoms/OuterLink'
import RecommendList from '../organisms/RecommendList'
import ReviewList from '../organisms/ReviewList'
import SEO from '../molecules/SEO'
import useMenu from '../hooks/useMenu'
import { CategoryListItem } from '../organisms/CategoryList'
import { SidebarLeft, SidebarRight } from '../organisms/Sidebar'
import { MysqlCategories, MysqlRanksEdge, Query } from '../graphqlTypes'

const Container = styled.div`
  margin: 20px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: flex-start;
    display: flex;
    margin: 40px 0 0;
  }
`

const Article = styled.article`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    flex: 1;
    margin: 0 30px 0;
  }
`

const PageHeading = styled.h1`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 14px;
  font-weight: bold;
  margin: 0 16px;
  text-align: center;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 20px;
    margin: 0;
  }
`

const PageDescription = styled.p`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 10px;
  margin: 12px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 12px;
    margin: 15px 0 0;
  }
`

const ProductList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 32px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 35px 0 0;
  }
`

const ProductListItem = styled.li`
  margin: 44px 0 0;
  &:first-child {
    margin: 0;
  }
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 56px 0 0;
  }
`

const ProductHeading = styled.div`
  align-items: center;
  display: flex;
`

const ProductHeadingLeft = styled.div`
  width: 70px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 85px;
  }
`

const EmblemImage = styled.img`
  width: 100%;
`

const ProductHeadingRight = styled.div`
  margin: 0 0 0 12px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 15px;
  }
`

const Manufacturer = styled.p`
  color: ${({ theme }) => theme.palette.font.sub};
  font-size: 10px;
  font-weight: bold;
  margin: 0 0 4px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 16px;
  }
`

const ProductName = styled.h2`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 14px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 20px;
  }
`

const Product = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: start;
    display: flex;
    margin: 18px 0 0;
  }
`

const ProductImageLink = styled(Link)`
  display: block;
  text-decoration: none;
`

const ProductImage = styled.img`
  display: block;
  margin: 12px auto 0;
  object-fit: contain;
  width: 100%;
  max-height: 160px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 280px;
    height: 280px;
    margin: 0 auto;
  }
`

const ProductDetail = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 20px;
  }
`

const ItemizedReview = styled.div`
  margin: 24px 0 0;
`

const Label = styled.label`
  color: ${({ theme }) => theme.palette.font.dark};
  display: inline-block;
  font-size: 12px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
  }
`

const ItemizedReviewListContainer = styled.div`
  margin: 11px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 7px 0 0;
  }
`

const RecommendListContainer = styled.div`
  margin: 20px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
  }
`

const PriceContainer = styled.div`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.palette.border.main};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.main};
  display: flex;
  justify-content: flex-end;
  margin: 12px 0 0;
  padding: 16px 0;
  text-align: right;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    border-bottom: 0;
  }
`

const PriceText = styled.span`
  font-size: 12px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
  }
`

const ProductAddition = styled.p`
  color: ${({ theme }) => theme.palette.font.gray};
  display: inline-block;
  font-size: 12px;
  margin: 7px 0 0;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 10px 0 0;
    text-align: right;
  }
`

const Review = styled.div`
  margin: 36px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px 0 0;
  }
`

const ReviewListContainer = styled.div`
  margin: 12px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 16px 0 0;
  }
`

const ProductLink = styled(Link)`
  display: block;
  margin: 26px 0 0;
  text-decoration: none;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px auto 0;
    width: 350px;
  }
`

const ButtonAngle = styled(Angle)`
  width: 24px;
  height: 24px;
  margin: 0 0 0 5px;
  object-fit: cover;
`

const SocialShare = styled.aside`
  margin: 44px 20px 4px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 98px 0 0;
  }
`

const SocialShareBalloon = styled.div`
  margin: 0 auto;
  width: 250px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
    width: 170px;
  }
`

const SocialShareButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 26px;
  }
`

const SocialShareButton = styled(OuterLink)`
  display: block;
  margin: 0 0 0 15px;
  text-decoration: none;
  width: 160px;
  &:first-child {
    margin: 0;
  }
`

const RelationalCategory = styled.div`
  margin: 32px 0 -14px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const RelationalCategoryList = styled.ul`
  list-style: none;
`

const RelationalCategoryListItem = styled(CategoryListItem)`
  &:last-child {
    border-width: 1px;
  }
`

type Props = ReplaceComponentRendererArgs['props'] & { pageContext: { category: MysqlCategories } } & { data: Query }

export default function CategoryTemplate(props: Props) {
  const { allMysqlCategories, allMysqlRanks, allMysqlSurveyQuestions, allMysqlSurveyAnswers, site } = props.data
  const { category } = props.pageContext

  const parentCategory = allMysqlCategories.edges.find(({ node }) => category.parent_id === node.mysqlId)
  if (!parentCategory) throw new TypeError('"parentCategory" is undefined')

  const ranks = allMysqlRanks.group.find(({ fieldValue }) => category.mysqlId === parseInt(fieldValue as string, 10))
  if (!ranks) throw new TypeError('"ranks" is undefined')

  const children = parentCategory.node.categories as MysqlCategories[]
  const prefix = category.title && category.title.includes('おすすめ') ? '' : 'おすすめ '
  const pageTitle = `${prefix}${category.title}部門 2020`

  const menu = useMenu(allMysqlCategories.edges, allMysqlRanks.group)

  return (
    <Layout menu={menu}>
      <SEO
        description={category.description!}
        image={`${process.env.GATSBY_CDN_URL}/${process.env.GATSBY_CDN_PATH}/ogp/top.png`}
        title={`${pageTitle} | ${site!.siteMetadata!.title}`}
        type="article"
        url={'https://award.mamari.jp' + category.fields!.pagePath!}
      />
      <Container>
        <SidebarLeft categories={menu} />
        <Article>
          <PageHeading>{pageTitle}</PageHeading>
          <PageDescription>{category.description}</PageDescription>

          <ProductList>
            {ranks.edges.map(({ node }: MysqlRanksEdge) => {
              if (!node.products) throw new TypeError('"product" is undefined')

              const product = node.products!
              const surveyAnswers = allMysqlSurveyAnswers.edges
                .filter(({ node }) => product.mysqlId === node.product_id && node.users != null)
                .slice(0, 2)

              return (
                <ProductListItem key={node.id}>
                  <ProductHeading>
                    <ProductHeadingLeft>
                      <LazyLoadComponent>
                        <EmblemImage
                          alt={`${product.name} エンブレム`}
                          src={`${product.fields!.emblemImageUrl}?auto=format`}
                        />
                      </LazyLoadComponent>
                    </ProductHeadingLeft>
                    <ProductHeadingRight>
                      <Manufacturer>{product.manufacturer}</Manufacturer>
                      <ProductName>{product.name}</ProductName>
                    </ProductHeadingRight>
                  </ProductHeading>

                  <Product>
                    <ProductImageLink to={product.fields!.pagePath!}>
                      <LazyLoadComponent>
                        <ProductImage alt={product.name!} src={`${product.fields!.imageUrl}?auto=format&max-h=280`} />
                      </LazyLoadComponent>
                    </ProductImageLink>

                    <ProductDetail>
                      <RecommendListContainer>
                        <RecommendList product={product} />
                      </RecommendListContainer>

                      <ItemizedReview>
                        <Label>項目別評価</Label>
                        <ItemizedReviewListContainer>
                          <ItemizedReviewList
                            product={product}
                            ranks={allMysqlRanks.edges}
                            surveyQuestions={allMysqlSurveyQuestions.edges}
                          />
                        </ItemizedReviewListContainer>
                      </ItemizedReview>

                      <PriceContainer>
                        <PriceText>{product.display_price}</PriceText>
                      </PriceContainer>

                      {product.addition != null && product.addition.length > 0 && (
                        <ProductAddition>{nl2br(product.addition!)}</ProductAddition>
                      )}
                    </ProductDetail>
                  </Product>

                  <Review>
                    <Label>ママたちの口コミ</Label>
                    <ReviewListContainer>
                      <ReviewList basic compact omitComment surveyAnswers={surveyAnswers} />
                    </ReviewListContainer>
                  </Review>

                  <ProductLink to={product.fields!.pagePath!}>
                    <Button color="white">
                      商品の詳細・口コミをみる
                      <ButtonAngle color="pink" direction="right" />
                    </Button>
                  </ProductLink>
                </ProductListItem>
              )
            })}
          </ProductList>
        </Article>
        <SidebarRight />
      </Container>

      <SocialShare>
        <SocialShareBalloon>
          <Image alt="パパや友達にオススメしてね！" filename="balloon-share.png" />
        </SocialShareBalloon>
        <SocialShareButtonGroup>
          <SocialShareButton
            to={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(
              'https://award.mamari.jp/2020_spring/features/award_all'
            )}`}
          >
            <Image filename="share-line.png" />
          </SocialShareButton>
          <SocialShareButton
            to={`https://twitter.com/share?url=${encodeURIComponent(
              'https://award.mamari.jp/2020_spring/features/award_all'
            )}&text=${encodeURIComponent('3,653件の口コミから選ばれた、#ママリ口コミ大賞 アイテム一気見せ！→')}`}
          >
            <Image filename="share-twitter.png" />
          </SocialShareButton>
        </SocialShareButtonGroup>
      </SocialShare>

      {children.length > 1 && (
        <RelationalCategory>
          <Heading>「{parentCategory.node.title}」の部門一覧</Heading>
          <RelationalCategoryList>
            {children
              .filter((child) => category.mysqlId !== child.mysqlId)
              .map((child) => (
                <RelationalCategoryListItem large category={child} to={child.fields!.pagePath!} key={child.id} />
              ))}
          </RelationalCategoryList>
        </RelationalCategory>
      )}
    </Layout>
  )
}

export const query = graphql`
  query($awardId: Int) {
    allMysqlCategories(filter: { parent_id: { eq: null } }) {
      edges {
        node {
          id
          mysqlId
          title
          categories {
            id
            mysqlId
            title
            fields {
              imageUrl
              pagePath
            }
          }
        }
      }
    }
    allMysqlRanks(filter: { award_id: { eq: $awardId } }, sort: { fields: rank, order: ASC }) {
      edges {
        node {
          id
          category_id
          product_id
          rank
          ratings
          total_rating
        }
      }
      group(field: category_id) {
        fieldValue
        edges {
          node {
            id
            mysqlId
            product_id
            rank
            ratings
            products {
              id
              mysqlId
              category_id
              name
              display_price
              manufacturer
              recommendation
              addition
              fields {
                emblemImageUrl
                imageUrl
                pagePath
              }
            }
          }
        }
      }
    }
    allMysqlSurveyQuestions(filter: { award_id: { eq: $awardId } }) {
      edges {
        node {
          id
          mysqlId
          content
        }
      }
    }
    allMysqlSurveyAnswers(
      filter: { award_id: { eq: $awardId }, is_sensitive: { eq: 0 } }
      sort: { fields: [display_position], order: ASC }
    ) {
      edges {
        node {
          id
          product_id
          ratings
          comment
          using_time
          users {
            mother_job
            mother_birthday
            youngest_child_birthday
            youngest_child_sex
            other_children
            created
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
